import React, { useState, useEffect } from 'react';
/**
 * API Hooks Index File
 * Tüm React hooks'ların export edildiği merkezi dosya
 */

// Import all hooks
import apiHooks, { 
  QueryKeyFactory,
  defaultQueryOptions,
  defaultMutationOptions 
} from './useApiQuery';

import authHooks from './useAuth';
import type { PaginatedResponse } from '../types/api.types';

// Import diğer hook'lar (eğer oluşturduysan)
// import { useSocket } from './useSocket';
// import { useWebSocket } from './useWebSocket';

// ==================== HOOK CATEGORIES ====================

/**
 * Core query hooks
 */
export const queryHooks = {
  // Generic hooks
  useApiQuery: apiHooks.useApiQuery,
  useApiMutation: apiHooks.useApiMutation,
  useApiInfiniteQuery: apiHooks.useApiInfiniteQuery,
  
  // Query utilities
  useQueryUtils: apiHooks.useQueryUtils,
  usePrefetch: apiHooks.usePrefetch,
  useQueryErrorHandler: apiHooks.useQueryErrorHandler,
  
  // Factories
  QueryKeyFactory,
  defaultQueryOptions,
  defaultMutationOptions
};

/**
 * Authentication hooks
 */
export const authHooksExport = {
  // Core auth
  useAuth: authHooks.useAuth,
  useProtectedRoute: authHooks.useProtectedRoute,
  useGuestRoute: authHooks.useGuestRoute,
  
  // Permissions & RBAC
  usePermissions: authHooks.usePermissions,
  useRBAC: authHooks.useRBAC,
  
  // Session & Tenant
  useTenant: authHooks.useTenant,
  useSession: authHooks.useSession,
  useAuthPolling: authHooks.useAuthPolling,
  
  // Forms & UI
  useLoginForm: authHooks.useLoginForm,
  useLogoutWithConfirmation: authHooks.useLogoutWithConfirmation,
  useAutoLogin: authHooks.useAutoLogin
};

/**
 * Data hooks (by entity)
 */
export const dataHooks = {
  // Auth
  useAuthProfile: apiHooks.useAuthProfile,
  useLogin: apiHooks.useLogin,
  useLogout: apiHooks.useLogout,
  
  // Alarms
  useAlarms: apiHooks.useAlarms,
  useAlarm: apiHooks.useAlarm,
  useActiveAlarms: apiHooks.useActiveAlarms,
  useRecentAlarms: apiHooks.useRecentAlarms,
  useAlarmStatistics: apiHooks.useAlarmStatistics,
  useAcknowledgeAlarms: apiHooks.useAcknowledgeAlarms,
  useResolveAlarms: apiHooks.useResolveAlarms,
  useOptimisticAcknowledgeAlarms: apiHooks.useOptimisticAcknowledgeAlarms,
  
  // Units
  useUnits: apiHooks.useUnits,
  useUnit: apiHooks.useUnit,
  useUnitsRequiringAttention: apiHooks.useUnitsRequiringAttention,
  useUnitPerformance: apiHooks.useUnitPerformance,
  useUnitAlarms: apiHooks.useUnitAlarms,
  useUnitWagons: apiHooks.useUnitWagons,
  useUpdateUnit: apiHooks.useUpdateUnit,
  
  // Fleets
  useFleets: apiHooks.useFleets,
  useFleet: apiHooks.useFleet,
  
  // Wagons
  useWagons: apiHooks.useWagons,
  useWagon: apiHooks.useWagon,
  
  // Users
  useUsers: apiHooks.useUsers,
  useUser: apiHooks.useUser,
  
  // Notifications
  useNotifications: apiHooks.useNotifications,
  useUnreadNotifications: apiHooks.useUnreadNotifications,
  useMarkNotificationAsRead: apiHooks.useMarkNotificationAsRead,
  
  // Infinite Queries
  useInfiniteAlarms: apiHooks.useInfiniteAlarms,
  useInfiniteUnits: apiHooks.useInfiniteUnits
};

/**
 * Feature-based hook groups
 */
export const featureHooks = {
  // Dashboard features
  dashboard: {
    useDashboardData: () => {
      const alarms = apiHooks.useAlarms({ limit: 10 });
      const unitsRequiringAttention = apiHooks.useUnitsRequiringAttention();
      const alarmStats = apiHooks.useAlarmStatistics();
      
      return {
        alarms,
        unitsRequiringAttention,
        alarmStats,
        isLoading: alarms.isLoading || unitsRequiringAttention.isLoading || alarmStats.isLoading,
        error: alarms.error || unitsRequiringAttention.error || alarmStats.error
      };
    },
    
    useQuickStats: () => {
      const activeAlarms = apiHooks.useActiveAlarms({ limit: 5 });
      const recentAlarms = apiHooks.useRecentAlarms(5);
      const unitsAttention = apiHooks.useUnitsRequiringAttention({ limit: 5 });
      
      return {
        activeAlarms: activeAlarms.data || [],
        recentAlarms: recentAlarms.data || [],
        unitsAttention: unitsAttention.data?.data || [],
        isLoading: activeAlarms.isLoading || recentAlarms.isLoading || unitsAttention.isLoading
      };
    }
  },
  
  // Unit details features
  unitDetails: {
    useUnitDetails: (unitId: string) => {
      const unit = apiHooks.useUnit(unitId);
      const unitAlarms = apiHooks.useUnitAlarms(unitId, { limit: 10 });
      const unitPerformance = apiHooks.useUnitPerformance(unitId);
      const unitWagons = apiHooks.useUnitWagons(unitId);
      
      return {
        unit: unit.data,
        alarms: unitAlarms.data?.data || [],
        performance: unitPerformance.data,
        wagons: unitWagons.data || [],
        isLoading: unit.isLoading || unitAlarms.isLoading || unitPerformance.isLoading || unitWagons.isLoading,
        error: unit.error || unitAlarms.error || unitPerformance.error || unitWagons.error,
        refetch: () => {
          unit.refetch();
          unitAlarms.refetch();
          unitPerformance.refetch();
          unitWagons.refetch();
        }
      };
    },
    
    useUnitManagement: (unitId: string) => {
      const { mutate: updateUnit, isPending: isUpdating } = apiHooks.useUpdateUnit();
      
      return {
        updateUnit: (data: any) => updateUnit({ id: unitId, data }),
        isUpdating
      };
    }
  },
  
  // Alarm management features
  alarmManagement: {
    useAlarmDetails: (alarmId: string) => {
      const alarm = apiHooks.useAlarm(alarmId);
      const { mutate: acknowledge, isPending: isAcknowledging } = apiHooks.useAcknowledgeAlarms();
      const { mutate: resolve, isPending: isResolving } = apiHooks.useResolveAlarms();
      
      return {
        alarm: alarm.data,
        isLoading: alarm.isLoading,
        error: alarm.error,
        acknowledge: (comment?: string) => acknowledge({
          alarmIds: [alarmId],
          acknowledgedBy: 'current_user', // Bu kısım auth'dan alınmalı
          comment
        }),
        resolve: (resolution?: string, comment?: string) => resolve({
          alarmIds: [alarmId],
          resolvedBy: 'current_user',
          resolution,
          comment
        }),
        isAcknowledging,
        isResolving,
        refetch: alarm.refetch
      };
    },
    
    useAlarmFilters: () => {
      // Bu hook alarm filter state'ini yönetir
      const [filters, setFilters] = useState<any>({});
      const alarms = apiHooks.useAlarms(filters);
      
      return {
        filters,
        setFilters,
        alarms: alarms.data?.data || [],
        isLoading: alarms.isLoading,
        error: alarms.error,
        pagination: alarms.data?.meta
      };
    }
  },
  
  // User management features
  userManagement: {
    useUserList: (params?: any) => {
      const users = apiHooks.useUsers(params);
      const { data, refetch } = users;
      
      return {
        users: data?.data || [],
        pagination: data?.meta,
        isLoading: users.isLoading,
        error: users.error,
        refetch
      };
    }
  }
};

// ==================== HOOK UTILITIES ====================

/**
 * Hook factory - Dinamik hook oluşturma
 */
export class HookFactory {
  /**
   * Generic data fetching hook oluştur
   */
  static createDataHook<TData, TParams = any>(
    queryKey: (params?: TParams) => any[],
    fetcher: (params?: TParams) => Promise<TData>,
    options?: any
  ) {
    return (params?: TParams) => {
      return apiHooks.useApiQuery(
        queryKey(params),
        () => fetcher(params),
        options
      );
    };
  }
  
  /**
   * Generic mutation hook oluştur
   */
  static createMutationHook<TData, TVariables = any>(
    mutator: (variables: TVariables) => Promise<TData>,
    options?: any
  ) {
    return () => {
      return apiHooks.useApiMutation(mutator, options);
    };
  }
  
  /**
   * Paginated data hook oluştur
   */
  static createPaginatedHook<TData, TParams = any>(
      queryKey: (params?: TParams) => any[],
      fetcher: (params?: TParams) => Promise<PaginatedResponse<TData>>,
      options?: any
    ) {
    return (params?: TParams) => {
        const result = apiHooks.useApiQuery(
        queryKey(params),
        () => fetcher(params),
        {
            ...options,
            // Paginated query'ler için ekstra ayarlar
            keepPreviousData: true, // Sayfa değişiminde önceki datayı tut
        }
        );
        
        // Pagination için ekstra bilgiler
        const paginationInfo = result.data?.meta || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
        };
        
        return {
        ...result,
        pagination: paginationInfo,
        data: result.data?.data || [],
        hasNextPage: paginationInfo.hasNext,
        hasPreviousPage: paginationInfo.hasPrev,
        currentPage: paginationInfo.page,
        totalPages: paginationInfo.totalPages,
        totalItems: paginationInfo.total
        };
      };
    }
}

/**
 * Hook composer - Multiple hooks'u birleştirme
 */
export function composeHooks<T>(hooks: Array<() => T>): () => T[] {
  return () => hooks.map(hook => hook());
}

// ==================== CUSTOM HOOK EXAMPLES ====================

/**
 * Example: Real-time alarm subscription hook
 */

/**
 * Example: Debounced search hook
 */
export function useDebouncedSearch<T>(
  searchFn: (query: string) => Promise<T>,
  delay: number = 300
) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<T | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [query, delay]);
  
  // Search effect
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults(null);
      return;
    }
    
    const performSearch = async () => {
      setIsSearching(true);
      try {
        const searchResults = await searchFn(debouncedQuery);
        setResults(searchResults);
      } catch (error) {
        console.error('Search failed:', error);
        setResults(null);
      } finally {
        setIsSearching(false);
      }
    };
    
    performSearch();
  }, [debouncedQuery, searchFn]);
  
  return {
    query,
    setQuery,
    results,
    isSearching,
    debouncedQuery
  };
}

/**
 * Example: Local storage sync hook
 */
export function useLocalStorageSync<T>(
  key: string,
  initialValue: T,
  syncWithServer?: (value: T) => Promise<void>
) {
  const [value, setValue] = useState<T>(() => {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : initialValue;
  });
  
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
    
    if (syncWithServer) {
      syncWithServer(value).catch(console.error);
    }
  }, [key, value, syncWithServer]);
  
  return [value, setValue] as const;
}

// ==================== HOOK REGISTRY ====================

/**
 * Hook Registry - Dinamik hook yönetimi
 */
export class HookRegistry {
  private static readonly registry: Map<string, any> = new Map();
  
  static register(name: string, hook: any): void {
    this.registry.set(name, hook);
  }
  
  static get<T>(name: string): T | undefined {
    return this.registry.get(name);
  }
  
  static getAll(): Map<string, any> {
    return new Map(this.registry);
  }
  
  static has(name: string): boolean {
    return this.registry.has(name);
  }
  
  static remove(name: string): boolean {
    return this.registry.delete(name);
  }
  
  static clear(): void {
    this.registry.clear();
  }
}

// Register core hooks
HookRegistry.register('useApiQuery', apiHooks.useApiQuery);
HookRegistry.register('useApiMutation', apiHooks.useApiMutation);
HookRegistry.register('useAuth', authHooks.useAuth);

// ==================== EXPORT TYPES ====================

// Re-export types from apiHooks
export type {
  ApiResponse,
  PaginatedResponse,
  ApiErrorResponse,
  BaseQueryParams,
  ID
} from '../types/api.types';

// Re-export types from authHooks
export type {
  AuthState,
  PermissionCheck,
  UseAuthOptions
} from './useAuth';

// ==================== DEFAULT EXPORT ====================

/**
 * Tüm hooks'ları içeren ana nesne
 */
const hooks = {
  // Core hooks
  ...apiHooks,
  ...authHooks,
  
  // Categorized hooks
  queries: queryHooks,
  auth: authHooksExport,
  data: dataHooks,
  features: featureHooks,
  
  // Utilities
  factory: HookFactory,
  registry: HookRegistry,
  compose: composeHooks,
  
  // Examples
  useDebouncedSearch,
  useLocalStorageSync,
  
  // Initialization
  initialize: () => {
    console.log('API hooks initialized');
    
    // Hook initialization logic
    const event = new CustomEvent('hooks-initialized', {
      detail: { timestamp: new Date().toISOString() }
    });
    globalThis.dispatchEvent(event);
  }
};

export default hooks;

// ==================== GLOBAL TYPE EXTENSIONS ====================

declare global {
  interface Window {
    /**
     * Debug için - sadece development'ta kullanın
     */
    __HOOKS__?: typeof hooks;
  }
}

// Development için global erişim (sadece dev mode'da)
if (import.meta.env.DEV) {
  (globalThis as any).__HOOKS__ = hooks;
  console.log('API hooks registered globally as window.__HOOKS__');
}

// ==================== REACT QUERY PROVIDER SETUP ====================

/**
 * React Query Provider setup fonksiyonu
 * App.tsx'te kullanın
 */
export function setupQueryClient() {
  const { QueryClient } = require('@tanstack/react-query');
  const { ReactQueryDevtools } = require('@tanstack/react-query-devtools');
  
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        ...defaultQueryOptions(),
        // Global query ayarları
        retry: 1,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true
      },
      mutations: {
        ...defaultMutationOptions(),
        retry: 0
      }
    }
  });
  
  return {
    queryClient,
    QueryClientProvider: require('@tanstack/react-query').QueryClientProvider,
    ReactQueryDevtools
  };
}